import get from 'lodash/get'
import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom'
import type { EmailCampaign } from 'types/campaign'
import type { State } from 'types/redux'
import type { User } from 'types/user'
import { DEFAULT_LINE_HEIGHT } from '@disco/constants/content-editor'
import { getCampaign } from '@disco/state-management/selectors/campaigns'
import { getUser } from '@disco/state-management/selectors/user'
import { getFontFamily, getFontSize } from '@disco/content-editor/editor-utils'
import Heading from 'components/ui/heading'
import ModalWindow from 'components/ui/modal-window'
import ContentEditor from 'pages/campaigns/editor/content-editor'
import type { RootState } from 'store/configureStore'

type OwnProps = {
  params: {
    campaignId: string
  }
}

function mapStateToProps(state: RootState, ownProps: OwnProps) {
  return {
    campaign: getCampaign(state, {
      campaignId: parseInt(ownProps.params.campaignId),
    }),
    user: getUser(state),
  }
}

const connector = connect(mapStateToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

interface AdditionalProps {
  navigate: NavigateFunction
}

type Props = PropsFromRedux & OwnProps & AdditionalProps

class CampaignSummary extends React.Component<Props> {
  _onClose = () => {
    const {
      params: { campaignId },
    } = this.props
    this.props.navigate(`/campaigns/${campaignId}/stats/dashboard`)
  }

  render() {
    const {
      campaign,
      params: { campaignId },
      user,
    } = this.props
    if (!campaign) return null
    return (
      <ModalWindow
        header={
          <Heading>
            <span translate="no">{campaign.name}</span>
          </Heading>
        }
        isOpen={true}
        onClose={this._onClose}
      >
        <div className="modal-content-wrapper">
          <ContentEditor
            className="email-content"
            readOnly
            campaign={{
              id: parseInt(campaignId, 10),
              status: campaign.status,
              fontFamily: campaign.template_data
                ? getFontFamily(null, campaign.template_data.campaignEditor)
                : undefined,
              fontSize: campaign.template_data
                ? `${getFontSize(
                    null,
                    campaign.template_data.campaignEditor,
                  )}px`
                : undefined,
              lineHeight: get(
                campaign.template_data,
                'campaignEditor.lineHeight',
                DEFAULT_LINE_HEIGHT,
              ),
              horizontalPadding: get(
                campaign.template_data,
                'campaignEditor.horizontalPadding',
                '25',
              ),
              verticalPadding: get(
                campaign.template_data,
                'campaignEditor.verticalPadding',
                '12',
              ),
              sections: campaign.template_data
                ? campaign.template_data.campaignEditor.sections
                : [],
              generalBackground: get(
                campaign.template_data,
                'campaignEditor.generalBackground',
                '#fff',
              ),
            }}
            user={user}
          />
        </div>
      </ModalWindow>
    )
  }
}

const ConnectedCampaignSummary = connector(CampaignSummary)

function CampaignSummaryWithRouter() {
  const navigate = useNavigate()
  const params = useParams<{ campaignId: string }>()

  const props: OwnProps = {
    params: params as { campaignId: string },
  }

  return <ConnectedCampaignSummary {...props} navigate={navigate} />
}

export default CampaignSummaryWithRouter
